/**
 * Campfire Cart Drawer — storefront engine.
 * Talks to Shopify's real Ajax Cart API (/cart.js, /cart/add.js, /cart/change.js, /cart/update.js).
 * No build step — plain ES module-free script, safe to load with `defer`.
 */
(function () {
  'use strict';

  if (window.CampfireCart) return; // guard against double-init (theme editor re-renders)

  var ROOT_ID = 'campfire-cart-root';
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function formatWithDelimiters(numberCents, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;
    if (isNaN(numberCents)) numberCents = 0;
    var n = (numberCents / 100).toFixed(precision);
    var parts = n.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  }

  function formatMoney(cents, format) {
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = format && format.match(placeholderRegex);
    var token = match ? match[1] : 'amount';
    var value;
    switch (token) {
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = formatWithDelimiters(cents, 2, ' ', '.'); break;
      case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' '); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'"); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return (format || '${{amount}}').replace(placeholderRegex, value);
  }

  var AMOUNT_PLACEHOLDER = /\{\{\s*amount\s*\}\}/;
  function fillAmount(template, amountStr) {
    return template && AMOUNT_PLACEHOLDER.test(template) ? template.replace(AMOUNT_PLACEHOLDER, amountStr) : (template || '') + ' ' + amountStr;
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function qs(root, sel) { return root.querySelector(sel); }
  function qsa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  var CampfireCartDrawer = function (root) {
    this.root = root;
    this.settingsEl = qs(root, '[data-campfire-settings]');
    this.settings = JSON.parse(this.settingsEl.textContent || '{}');
    this.scrim = qs(root, '.campfire-cart-scrim');
    this.drawer = qs(root, '.campfire-cart-drawer');
    this.body = qs(root, '[data-campfire-body]');
    this.footerDynamic = qs(root, '[data-campfire-footer-dynamic]');
    this.expressSlot = qs(root, '[data-campfire-express]');
    this.toast = qs(root, '[data-campfire-toast]');
    this.cart = null;
    this.lastFocused = null;
    this.pendingLines = {};
    this.isOpen = false;
    this._giftDismissed = false;

    this._protectDismissed = false;
    try {
      this._giftDismissed = sessionStorage.getItem('campfireGiftDismissed') === '1';
      this._protectDismissed = sessionStorage.getItem('campfireProtectDismissed') === '1';
    } catch (e) { /* storage unavailable (private mode) */ }

    this.bindStaticEvents();
    this.bindOpenTriggers();
    this.refresh();
  };

  CampfireCartDrawer.prototype.bindStaticEvents = function () {
    var self = this;

    this.scrim.addEventListener('click', function () { self.close(); });
    qsa(this.root, '[data-campfire-close]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.close(); });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.isOpen) self.close();
      if (e.key === 'Tab' && self.isOpen) self.trapFocus(e);
    });

    // Delegate all dynamic-content clicks (qty steppers, remove, upsell add, toggles)
    this.root.addEventListener('click', function (e) {
      var stepBtn = e.target.closest('[data-campfire-step]');
      if (stepBtn) {
        var dir = stepBtn.getAttribute('data-campfire-step');
        var key = stepBtn.closest('[data-line-key]').getAttribute('data-line-key');
        var line = self.cart.items.find(function (it) { return it.key === key; });
        if (!line) return;
        var next = dir === 'inc' ? Math.min(9, line.quantity + 1) : Math.max(0, line.quantity - 1);
        self.changeLine(key, next);
        return;
      }
      var removeBtn = e.target.closest('[data-campfire-remove]');
      if (removeBtn) {
        self.changeLine(removeBtn.getAttribute('data-campfire-remove'), 0);
        return;
      }
      var addBtn = e.target.closest('[data-campfire-upsell-add]');
      if (addBtn) {
        self.addUpsell(addBtn);
        return;
      }
      var protectInfo = e.target.closest('[data-campfire-protect-info]');
      if (protectInfo) {
        protectInfo.classList.toggle('is-open');
        return;
      }
      var openTip = qs(self.root, '.campfire-protection__info.is-open');
      if (openTip) openTip.classList.remove('is-open');
      var protect = e.target.closest('[data-campfire-protect-toggle]');
      if (protect) {
        self.toggleProtection();
        return;
      }
      var noteToggle = e.target.closest('[data-campfire-note-toggle]');
      if (noteToggle) {
        self.toggleNotePanel();
        return;
      }
      var applyBtn = e.target.closest('[data-campfire-discount-apply]');
      if (applyBtn) {
        self.applyDiscount();
        return;
      }
      var removeDiscountBtn = e.target.closest('[data-campfire-discount-remove]');
      if (removeDiscountBtn) {
        self.removeDiscount();
        return;
      }
    });

    this.root.addEventListener('change', function (e) {
      if (e.target.matches('[data-campfire-note-field]')) {
        self.debouncedNote(e.target.value);
      }
    });
    this.root.addEventListener('keydown', function (e) {
      if (e.target.matches('[data-campfire-discount-field]') && e.key === 'Enter') {
        e.preventDefault();
        self.applyDiscount();
      }
    });

    document.addEventListener('shopify:section:unload', function (e) {
      if (e.target.contains(self.root)) self.destroy();
    });
  };

  CampfireCartDrawer.prototype.debouncedNote = debounce(function (value) {
    this.updateNote(value);
  }, 500);

  CampfireCartDrawer.prototype.bindOpenTriggers = function () {
    var self = this;
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-campfire-cart-toggle]');
      if (trigger) { e.preventDefault(); self.open(); return; }

      if (self.settings.autoBindCartLinks === false) return;
      var link = e.target.closest('a[href]');
      if (!link || link.hasAttribute('data-campfire-ignore')) return;
      var url = link.getAttribute('href') || '';
      if (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?cart\/?(?:[?#].*)?$/i.test(url)) {
        e.preventDefault();
        self.open();
      }
    });
  };

  CampfireCartDrawer.prototype.trapFocus = function (e) {
    var focusable = qsa(this.drawer, FOCUSABLE);
    if (!focusable.length) return;
    var first = focusable[0], last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  };

  CampfireCartDrawer.prototype.open = function () {
    this.lastFocused = document.activeElement;
    document.documentElement.classList.add('campfire-cart-open');
    this.root.setAttribute('aria-hidden', 'false');
    this.isOpen = true;
    var closeBtn = qs(this.root, '[data-campfire-close]');
    if (closeBtn) closeBtn.focus();
    document.dispatchEvent(new CustomEvent('campfire:cart:open'));
    var self = this;
    requestAnimationFrame(function () { self.fitProtectionRow(); });
  };

  CampfireCartDrawer.prototype.close = function () {
    document.documentElement.classList.remove('campfire-cart-open');
    this.root.setAttribute('aria-hidden', 'true');
    this.isOpen = false;
    if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus();
    document.dispatchEvent(new CustomEvent('campfire:cart:close'));
  };

  CampfireCartDrawer.prototype.toggleNotePanel = function () {
    var panel = qs(this.root, '[data-campfire-note-panel]');
    if (!panel) return;
    var open = panel.getAttribute('data-open') === 'true';
    panel.setAttribute('data-open', open ? 'false' : 'true');
    if (!open) {
      var field = qs(panel, '[data-campfire-note-field]');
      if (field) field.focus();
    }
  };

  CampfireCartDrawer.prototype.showToast = function (msg) {
    var el = this.toast;
    if (!el) return;
    el.textContent = msg;
    el.setAttribute('data-visible', 'true');
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(function () { el.setAttribute('data-visible', 'false'); }, 2600);
  };

  // ---------- Network ----------

  CampfireCartDrawer.prototype.request = function (url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          var err = new Error(data.message || data.description || 'Cart request failed');
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  };

  CampfireCartDrawer.prototype.refresh = function () {
    var self = this;
    return fetch(this.settings.routes.cartUrl + '.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) { self.setCart(cart); return cart; })
      .catch(function () { self.renderError(); });
  };

  CampfireCartDrawer.prototype.setCart = function (cart) {
    this.cart = cart;
    this.render();
    this.syncCountBadges();
    this.maybeAutoGift();
    this.maybeAutoProtect();
    document.dispatchEvent(new CustomEvent('campfire:cart:updated', { detail: { cart: cart } }));
  };

  CampfireCartDrawer.prototype.changeLine = function (key, quantity) {
    var self = this;
    this.pendingLines[key] = true;
    this.render();
    return this.request(this.settings.routes.cartChangeUrl, { id: key, quantity: quantity })
      .then(function (cart) { delete self.pendingLines[key]; self.setCart(cart); })
      .catch(function (err) {
        delete self.pendingLines[key];
        self.showToast(err.message || 'Could not update cart');
        self.refresh();
      });
  };

  CampfireCartDrawer.prototype.addUpsell = function (btn) {
    var self = this;
    var variantId = Number(btn.getAttribute('data-campfire-upsell-add'));
    btn.disabled = true;
    btn.textContent = '…';
    return this.request(this.settings.routes.cartAddUrl, { items: [{ id: variantId, quantity: 1 }] })
      .then(function () {
        self.showToast('Added to cart');
        return self.refresh();
      })
      .catch(function (err) {
        self.showToast(err.message || 'Couldn’t add that item');
      })
      .finally(function () {
        btn.disabled = false;
        btn.textContent = 'Add';
      });
  };

  CampfireCartDrawer.prototype.toggleProtection = function () {
    var self = this;
    var variantId = this.settings.insuranceVariantId;
    if (!variantId) { this.showToast('Set up a Shipping Protection product in the Theme Editor to enable this feature.'); return; }
    var line = this.cart.items.find(function (it) { return it.variant_id === variantId; });
    var turningOn = !line;

    var toggleEl = qs(this.root, '[data-campfire-protect-toggle]');
    if (toggleEl) toggleEl.setAttribute('data-on', turningOn ? 'true' : 'false'); // optimistic

    this._protectDismissed = !turningOn;
    try { sessionStorage.setItem('campfireProtectDismissed', turningOn ? '0' : '1'); } catch (e) {}

    var promise = turningOn
      ? this.request(this.settings.routes.cartAddUrl, { items: [{ id: variantId, quantity: 1 }] })
      : this.request(this.settings.routes.cartChangeUrl, { id: line.key, quantity: 0 });

    promise.then(function () {
      self.refresh();
    }).catch(function (err) {
      self.showToast(err.message || 'Could not update order protection');
      self.refresh();
    });
  };

  CampfireCartDrawer.prototype.maybeAutoProtect = function () {
    if (!this.settings.insuranceVariantId || !this.cart || this._addingProtection) return;
    var variantId = this.settings.insuranceVariantId;
    var line = this.cart.items.find(function (it) { return it.variant_id === variantId; });
    var realCount = this.realItemCount(this.cart);
    var self = this;

    if (realCount === 0 && line) {
      // Nothing left to protect — drop the dangling protection line instead of charging for an empty order.
      this.changeLine(line.key, 0);
      return;
    }

    if (!this.settings.insuranceEnabled || !this.settings.insuranceDefaultOn) return;
    if (this._protectDismissed || line || realCount === 0) return;

    this._addingProtection = true;
    this.request(this.settings.routes.cartAddUrl, { items: [{ id: variantId, quantity: 1 }] })
      .then(function () { self._addingProtection = false; self.refresh(); })
      .catch(function () { self._addingProtection = false; });
  };

  CampfireCartDrawer.prototype.maybeAutoGift = function () {
    if (!this.settings.autoAddGift || !this.settings.giftVariantId || !this.cart) return;
    var subtotal = this.merchandiseSubtotal(this.cart);
    var threshold = this.settings.giftThreshold * 100;
    var hasGift = this.cart.items.some(function (it) { return it.variant_id === this.settings.giftVariantId; }, this);
    var self = this;

    if (subtotal >= threshold && !hasGift && !this._giftDismissed && !this._addingGift) {
      this._addingGift = true;
      this.request(this.settings.routes.cartAddUrl, { items: [{ id: this.settings.giftVariantId, quantity: 1 }] })
        .then(function () { self._addingGift = false; self.refresh(); })
        .catch(function () { self._addingGift = false; });
    } else if (subtotal < threshold && hasGift) {
      var line = this.cart.items.find(function (it) { return it.variant_id === this.settings.giftVariantId; }, this);
      if (line) this.changeLine(line.key, 0);
    }
  };

  CampfireCartDrawer.prototype.updateNote = function (note) {
    var self = this;
    return this.request(this.settings.routes.cartUpdateUrl, { note: note })
      .then(function (cart) { self.cart = cart; })
      .catch(function (err) { self.showToast(err.message || 'Could not save note'); });
  };

  CampfireCartDrawer.prototype.applyDiscount = function () {
    var self = this;
    var field = qs(this.root, '[data-campfire-discount-field]');
    if (!field || !field.value.trim()) return;
    var code = field.value.trim();
    var btn = qs(this.root, '[data-campfire-discount-apply]');
    if (btn) { btn.disabled = true; btn.textContent = '…'; }

    return this.request(this.settings.routes.cartUpdateUrl, { discount: code })
      .then(function (cart) {
        self.cart = cart;
        var applied = (cart.discount_codes || []).find(function (d) { return d.code.toLowerCase() === code.toLowerCase(); });
        if (applied && applied.applicable === false) {
          // Auto-remove the invalid code
          self._discountError = 'Discount code "' + code + '" is not valid';
          return self.request(self.settings.routes.cartUpdateUrl, { discount: '' })
            .then(function (cleaned) {
              self.cart = cleaned;
              self.render();
            });
        } else {
          self._discountError = null;
          self.showToast('Discount applied');
          self.render();
        }
      })
      .catch(function (err) { self.showToast(err.message || 'Could not apply that code'); })
      .finally(function () { if (btn) { btn.disabled = false; btn.textContent = 'Apply'; } });
  };

  CampfireCartDrawer.prototype.removeDiscount = function () {
    var self = this;
    return this.request(this.settings.routes.cartUpdateUrl, { discount: '' })
      .then(function (cart) { self.cart = cart; self._discountError = null; self.render(); })
      .catch(function (err) { self.showToast(err.message || 'Could not remove code'); });
  };

  CampfireCartDrawer.prototype.syncCountBadges = function () {
    var count = this.cart ? this.realItemCount(this.cart) : 0;
    qsa(document, '[data-campfire-cart-count]').forEach(function (el) {
      el.textContent = String(count);
      el.hidden = count === 0 && el.hasAttribute('data-hide-when-zero');
    });
  };

  CampfireCartDrawer.prototype.renderError = function () {
    this.body.innerHTML = '<div class="campfire-empty"><span class="campfire-empty__icon">⚠️</span><span class="campfire-empty__title">Couldn’t load your cart</span></div>';
  };

  // ---------- Rendering ----------

  CampfireCartDrawer.prototype.realItemCount = function (cart) {
    var insuranceVariantId = this.settings.insuranceVariantId;
    return cart.items.reduce(function (sum, it) { return sum + (it.variant_id === insuranceVariantId ? 0 : it.quantity); }, 0);
  };

  CampfireCartDrawer.prototype.merchandiseSubtotal = function (cart) {
    var insuranceVariantId = this.settings.insuranceVariantId;
    var insuranceLine = cart.items.find(function (it) { return it.variant_id === insuranceVariantId; });
    return cart.total_price - (insuranceLine ? insuranceLine.final_line_price : 0);
  };

  CampfireCartDrawer.prototype.render = function () {
    if (!this.cart) {
      this.body.innerHTML = '<div class="campfire-cart__loading"><div class="campfire-spinner" role="status" aria-label="Loading cart"></div></div>';
      return;
    }
    var cart = this.cart, s = this.settings;
    var fmt = function (cents) { return formatMoney(cents, s.moneyFormat); };
    var realCount = this.realItemCount(cart);

    qs(this.root, '[data-campfire-count]').textContent = realCount + (realCount === 1 ? ' item' : ' items');

    if (realCount === 0) {
      this.body.innerHTML =
        '<div class="campfire-empty">' +
          '<span class="campfire-empty__icon">🎒</span>' +
          '<span class="campfire-empty__title">Your cart is empty</span>' +
          '<a href="' + escapeHtml(s.routes.allProductsUrl || '/collections/all') + '" class="campfire-empty__cta">Start shopping</a>' +
        '</div>';
      this.renderFooter(cart, fmt, true);
      return;
    }

    var subtotal = this.merchandiseSubtotal(cart);
    var html = '';
    var freeshipHtml = '';

    s.blocks.forEach(function (block) {
      if (block.type === 'free_shipping_bar' && s.freeShippingEnabled) {
        freeshipHtml += this.renderFreeShip(subtotal, fmt);
      } else if (block.type === 'rewards' && s.giftEnabled) {
        html += this.renderReward(subtotal);
      } else if (block.type === 'upsell' && s.upsellProducts && s.upsellProducts.length) {
        html += this.renderUpsell(fmt);
      } else if (block.type === 'order_note' && s.noteEnabled) {
        html += this.renderNote(cart);
      } else if (block.type === 'discount_code' && s.discountEnabled) {
        html += this.renderDiscount(cart, fmt);
      }
    }, this);

    html = freeshipHtml + this.renderItems(cart, fmt) + html;

    this.body.innerHTML = html;
    this.renderFooter(cart, fmt, false);
    // Wire up copy-code buttons
    var self = this;
    this.body.querySelectorAll('[data-campfire-copy-code]').forEach(function(el) {
      el.addEventListener('click', function() {
        var code = el.getAttribute('data-campfire-copy-code');
        if (navigator.clipboard) {
          navigator.clipboard.writeText(code).then(function() {
            el.querySelector('.campfire-freeship__bonus-copy').textContent = '✓';
            setTimeout(function() { el.querySelector('.campfire-freeship__bonus-copy').textContent = '⧉'; }, 2000);
          });
        }
      });
    });
  };

  CampfireCartDrawer.prototype.renderFreeShip = function (subtotal, fmt) {
    var threshold = this.settings.freeShippingThreshold * 100;
    var remaining = Math.max(0, threshold - subtotal);
    var pct = threshold > 0 ? Math.min(100, Math.round((subtotal / threshold) * 100)) : 100;
    var msg = remaining <= 0
      ? this.settings.strings.freeShipReached
      : fillAmount(this.settings.strings.freeShipRemaining, fmt(remaining));

    // Build bonus unlock strip (shown only when threshold is met and feature is enabled)
    var bonusHtml = '';
    if (remaining <= 0 && this.settings.freeshipUnlockEnabled) {
      var unlockMsg = this.settings.freeshipUnlockMessage || '';
      var unlockCode = this.settings.freeshipUnlockCode || '';
      var codeLabel = this.settings.freeshipUnlockCodeLabel || 'Use code at checkout:';
      bonusHtml =
        '<div class="campfire-freeship__bonus">' +
          (unlockMsg ? '<div class="campfire-freeship__bonus-msg">' + escapeHtml(unlockMsg) + '</div>' : '') +
          (unlockCode
            ? '<div class="campfire-freeship__bonus-code">' +
                '<span class="campfire-freeship__bonus-label">' + escapeHtml(codeLabel) + '</span>' +
                '<span class="campfire-freeship__bonus-pill" data-campfire-copy-code="' + escapeHtml(unlockCode) + '">' +
                  escapeHtml(unlockCode) +
                  '<span class="campfire-freeship__bonus-copy" aria-label="Copy code">⧉</span>' +
                '</span>' +
              '</div>'
            : '') +
        '</div>';
    }

    return (
      '<div class="campfire-freeship">' +
        '<div class="campfire-freeship__msg">' + escapeHtml(msg) + '</div>' +
        '<div class="campfire-freeship__track"><div class="campfire-freeship__fill" style="width:' + pct + '%"></div></div>' +
        bonusHtml +
      '</div>'
    );
  };

  CampfireCartDrawer.prototype.renderReward = function (subtotal) {
    var threshold = this.settings.giftThreshold * 100;
    var remaining = Math.max(0, threshold - subtotal);
    var msg = remaining <= 0
      ? this.settings.strings.giftReached
      : fillAmount(this.settings.strings.giftRemaining, formatMoney(remaining, this.settings.moneyFormat));
    return (
      '<div class="campfire-reward">' +
        '<span class="campfire-reward__icon">🎁</span>' +
        '<span class="campfire-reward__msg">' + escapeHtml(msg) + '</span>' +
      '</div>'
    );
  };

  CampfireCartDrawer.prototype.renderItems = function (cart, fmt) {
    var self = this;
    var insuranceVariantId = this.settings.insuranceVariantId;
    var visibleItems = cart.items.filter(function (item) { return item.variant_id !== insuranceVariantId; });
    var hasPreorder = false;
    var rows = visibleItems.map(function (item) {
      var pending = self.pendingLines[item.key] ? 'true' : 'false';
      var img = item.image
        ? '<img src="' + escapeHtml(item.image.replace(/(\.[a-z]+)(\?.*)?$/i, '_120x$1$2')) + '" alt="' + escapeHtml(item.title) + '" loading="lazy" width="58" height="58">'
        : '';
      var isPreorder = !!(item.properties && item.properties['Pre-order']);
      if (isPreorder) hasPreorder = true;
      var preorderChip = isPreorder
        ? '<span class="campfire-line__preorder">' + escapeHtml(self.settings.strings.preorderBadge || 'Pre-order') + '</span>' : '';
      var variantLine = (item.variant_title || item.product_title !== item.title || isPreorder)
        ? '<span class="campfire-line__variant">' + escapeHtml(item.variant_title || '') + preorderChip + '</span>' : '';
      var removeBtn = '<button type="button" class="campfire-line__remove" data-campfire-remove="' + escapeHtml(item.key) + '" aria-label="Remove ' + escapeHtml(item.title) + '">Remove</button>';

      return (
        '<div class="campfire-line" data-line-key="' + escapeHtml(item.key) + '" data-pending="' + pending + '">' +
          '<div class="campfire-line__image">' + img + '</div>' +
          '<div class="campfire-line__info">' +
            '<div class="campfire-line__top">' +
              '<span class="campfire-line__title"><a href="' + escapeHtml(item.url) + '">' + escapeHtml(item.title) + '</a></span>' +
              '<span class="campfire-line__price">' + fmt(item.final_line_price) + '</span>' +
            '</div>' +
            variantLine +
            '<div class="campfire-line__row-bottom">' +
              '<div class="campfire-stepper">' +
                '<button type="button" data-campfire-step="dec" aria-label="Decrease quantity">−</button>' +
                '<span class="campfire-stepper__qty">' + item.quantity + '</span>' +
                '<button type="button" data-campfire-step="inc" aria-label="Increase quantity" ' + (item.quantity >= 9 ? 'disabled' : '') + '>+</button>' +
              '</div>' +
              removeBtn +
            '</div>' +
          '</div>' +
        '</div>'
      );
    });
    var preorderNote = '';
    if (hasPreorder && this.settings.strings.preorderNote) {
      preorderNote =
        '<div class="campfire-preorder-note">' +
          '<span class="campfire-preorder-note__icon">📦</span>' +
          '<span class="campfire-preorder-note__msg">' + escapeHtml(this.settings.strings.preorderNote) + '</span>' +
        '</div>';
    }
    return '<div class="campfire-cart__items">' + rows.join('') + '</div>' + preorderNote;
  };

  CampfireCartDrawer.prototype.renderUpsell = function (fmt) {
    var cards = this.settings.upsellProducts.map(function (p) {
      var img = p.image ? '<img src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="100" height="62">' : '';
      return (
        '<div class="campfire-upsell__card">' +
          '<div class="campfire-upsell__img">' + img + '</div>' +
          '<span class="campfire-upsell__title">' + escapeHtml(p.title) + '</span>' +
          '<div class="campfire-upsell__bottom">' +
            '<span class="campfire-upsell__price">' + fmt(p.price) + '</span>' +
            '<button type="button" class="campfire-upsell__add" data-campfire-upsell-add="' + p.variantId + '">Add</button>' +
          '</div>' +
        '</div>'
      );
    }).join('');
    return (
      '<div class="campfire-upsell">' +
        '<div class="campfire-upsell__heading">' + escapeHtml(this.settings.strings.upsellHeading) + '</div>' +
        '<div class="campfire-upsell__row">' + cards + '</div>' +
      '</div>'
    );
  };

  CampfireCartDrawer.prototype.renderNote = function (cart) {
    var note = cart.note || '';
    var open = note ? 'true' : 'false';
    return (
      '<button type="button" class="campfire-note" data-campfire-note-toggle>' +
        '<span class="campfire-note__icon">📝</span>' +
        '<span class="campfire-note__label">' + escapeHtml(this.settings.strings.noteLabel) + '</span>' +
        '<span class="campfire-note__plus">＋</span>' +
      '</button>' +
      '<div class="campfire-note__panel" data-campfire-note-panel data-open="' + open + '">' +
        '<textarea class="campfire-note__textarea" data-campfire-note-field placeholder="' + escapeHtml(this.settings.strings.notePlaceholder) + '">' + escapeHtml(note) + '</textarea>' +
      '</div>'
    );
  };

  CampfireCartDrawer.prototype.renderDiscount = function (cart, fmt) {
    var codes = cart.discount_codes || [];
    var apps = cart.discount_applications || [];
    if (codes.length) {
      var d = codes[codes.length - 1];
      // Find matching application for value info
      var app = apps.find(function(a) { return a.title && a.title.toLowerCase() === d.code.toLowerCase(); });
      var valueBadge = '';
      if (app) {
        if (app.value_type === 'percentage') {
          valueBadge = '<span class=\'campfire-discount__badge\'>' + parseFloat(app.value) + '% off</span>';
        } else if (app.value_type === 'fixed_amount') {
          var amt = Math.round(parseFloat(app.value) * 100);
          valueBadge = '<span class=\'campfire-discount__badge\'>-' + fmt(amt) + '</span>';
        } else if (app.value_type === 'shipping') {
          valueBadge = '<span class=\'campfire-discount__badge\'>Free shipping</span>';
        }
      }
      return (
        '<div class=\'campfire-discount__applied\'>' +
          '<div class=\'campfire-discount__applied-left\'>' +
            '<span class=\'campfire-discount__code\'>🏷️ ' + escapeHtml(d.code) + '</span>' +
            valueBadge +
          '</div>' +
          '<button type=\'button\' data-campfire-discount-remove class=\'campfire-discount__remove\'>✕</button>' +
        '</div>'
      );
    }
    var errorMsg = this._discountError ? '<div class=\'campfire-discount__error\'>' + escapeHtml(this._discountError) + '</div>' : '';
    return (
      '<div class=\'campfire-discount\'>' +
        '<input type=\'text\' class=\'campfire-discount__input\' data-campfire-discount-field placeholder=\'🏷️ Discount code\' autocomplete=\'off\'>' +
        '<button type=\'button\' class=\'campfire-discount__apply\' data-campfire-discount-apply>Apply</button>' +
      '</div>' +
      errorMsg
    );
  };CampfireCartDrawer.prototype.renderFooter = function (cart, fmt, isEmpty) {
    var s = this.settings;
    var protectionOn = !!(s.insuranceVariantId && cart.items.some(function (it) { return it.variant_id === s.insuranceVariantId; }));
    var fee = protectionOn ? (cart.items.find(function (it) { return it.variant_id === s.insuranceVariantId; }).final_line_price) : 0;
    var total = cart.total_price;

    var html = '';

    if (s.insuranceEnabled && !isEmpty) {
      var tipText = (s.strings.insuranceTooltip || '').trim();
      var infoHtml = tipText
        ? '<span class="campfire-protection__info" data-campfire-protect-info aria-label="Order protection details">i' +
            '<span class="campfire-protection__tooltip" role="tooltip">' + escapeHtml(tipText).replace(/\n/g, '<br>') + '</span>' +
          '</span>'
        : '';
      html +=
        '<div class="campfire-protection-card">' +
        '<button type="button" class="campfire-protection" data-campfire-protect-toggle aria-pressed="' + protectionOn + '">' +
          '<span class="campfire-toggle" data-campfire-protect-toggle data-on="' + protectionOn + '"><span class="campfire-toggle__knob"></span></span>' +
          '<span class="campfire-protection__label">🛡️ ' + escapeHtml(s.strings.insuranceLabel) + infoHtml + '</span>' +
          '<span class="campfire-protection__fee">' + (s.insuranceVariantId ? (protectionOn ? '+' + fmt(fee) : '+' + fmt(s.insurancePrice || 0)) : 'Set price in Theme Editor') + '</span>' +
        '</button>' +
        '</div>';
    }

  
        var appliedCodes = (cart.discount_codes || []).filter(function (c) { return c.applicable !== false; }).map(function (c) { return c.code; });
    if (!appliedCodes.length) {
      /* No code — fall back to automatic discount names (cart-level + line-level) */
      var autoTitles = {};
      (cart.cart_level_discount_applications || []).forEach(function (a) { if (a.title) autoTitles[a.title] = 1; });
      (cart.items || []).forEach(function (it) {
        (it.line_level_discount_allocations || []).forEach(function (al) {
          var t = al.discount_application && al.discount_application.title;
          if (t) autoTitles[t] = 1;
        });
      });
      appliedCodes = Object.keys(autoTitles);
    }
    if (cart.total_discount > 0 || appliedCodes.length) {
      var savingsLabel = appliedCodes.length ? '🏷 ' + escapeHtml(appliedCodes.join(', ')) : '🏷 You saved';
      var savingsValue = cart.total_discount > 0 ? '-' + fmt(cart.total_discount) : 'Applied';
      html += '<div class="campfire-savings"><span class="campfire-savings__label">' + savingsLabel + '</span><span class="campfire-savings__value">' + savingsValue + '</span></div>';
    }  // Afterpay installments line
    if (!isEmpty && total > 0) {
          var apAmt = Math.ceil(total / 4);
          var apLogoUrl = window._campfireAfterpaySrc || '';
          html += '<div class="campfire-afterpay">';
          html += '<span class="campfire-afterpay__text">or 4 interest-free payments of ' + fmt(apAmt) + ' with </span';
          if (apLogoUrl) {
            html += '<img src="' + apLogoUrl + '" class="campfire-afterpay__logo" alt="Afterpay" />';
          } else {
            html += '<span class="campfire-afterpay__badge">Afterpay</span>';
          }
          html += '</div>';
        }
    html +=
      '<a href="' + (s.routes.checkoutUrl || '/checkout') + '" class="campfire-checkout" ' + (isEmpty ? 'aria-disabled="true" tabindex="-1" style="pointer-events:none;opacity:.5;"' : '') + '>' +
        'Checkout · ' + fmt(total) + ' <span class="campfire-checkout__arrow">→</span>' +
      '</a>' +
      '<span class="campfire-fineprint">Shipping calculated at checkout</span>';

    this.footerDynamic.innerHTML = html;

    if (this.expressSlot) {
      var afterpayEl = this.footerDynamic.querySelector('.campfire-afterpay');
      if (afterpayEl) {
        this.footerDynamic.insertBefore(this.expressSlot, afterpayEl);
      } else {
        this.footerDynamic.appendChild(this.expressSlot);
      }
      this.expressSlot.style.display = (s.expressEnabled && !isEmpty) ? '' : 'none';
    }

    this.fitProtectionRow();
  };

  /* Scale the protection row's text down until label + fee fit inside the pill
     (the label is nowrap, so on narrow drawers the fee otherwise overflows). */
  CampfireCartDrawer.prototype.fitProtectionRow = function () {
    var prot = this.footerDynamic && qs(this.footerDynamic, '.campfire-protection');
    if (!prot || !prot.clientWidth) return;
    var lbl = qs(prot, '.campfire-protection__label');
    var fee = qs(prot, '.campfire-protection__fee');
    if (!lbl || !fee) return;
    /* the (hidden) tooltip is abs-positioned and up to 230px wide, which
       inflates scrollWidth — take it out of the measurement entirely */
    var tips = prot.querySelectorAll('.campfire-protection__tooltip');
    tips.forEach(function (t) { t.style.display = 'none'; });
    lbl.style.fontSize = '';
    fee.style.fontSize = '';
    var size = parseFloat(getComputedStyle(lbl).fontSize) || 13;
    var guard = 16;
    while (prot.scrollWidth > prot.clientWidth && size > 8 && guard--) {
      size -= 0.5;
      lbl.style.fontSize = size + 'px';
      fee.style.fontSize = size + 'px';
    }
    tips.forEach(function (t) { t.style.display = ''; });
  };

  CampfireCartDrawer.prototype.destroy = function () {
    // no persistent timers/observers beyond what GC handles on root removal
  };

  function init() {
    var root = document.getElementById(ROOT_ID);
    if (!root) return;
    var instance = new CampfireCartDrawer(root);
    window.CampfireCart = {
      open: function () { instance.open(); },
      close: function () { instance.close(); },
      refresh: function () { return instance.refresh(); },
      _instance: instance
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector && e.target.querySelector('#' + ROOT_ID)) {
      window.CampfireCart = null;
      init();
    }
  });
})();